import React, { useState } from "react";
import { LuMic, LuSquare } from "react-icons/lu";
import { cn, notify, Spinner } from "../../components/ui";
import { useSession } from "../../context/SessionContext";
import { useApi } from "../../hooks/useApi";
import { t } from "../../i18n";
import { chatService, ConsultationFields } from "../../services/chat";
import { useAudioRecorder } from "./useAudioRecorder";

interface ConsultationRecorderProps {
  onFields: (fields: ConsultationFields) => void;
}

// Gravação da consulta: toca para gravar, toca de novo para encerrar; o áudio
// vai para a IA, que devolve os campos do pedido.
export const ConsultationRecorder: React.FC<ConsultationRecorderProps> = ({ onFields }) => {
  const recorder = useAudioRecorder("consulta");
  const { request, apiFetchForm } = useApi<{ json?: ConsultationFields }>();
  const { metadata } = useSession();
  const [phase, setPhase] = useState<"idle" | "transcribing" | "done">("idle");

  const toggle = async () => {
    if (!recorder.recording) return recorder.start();
    const audio = await recorder.stop();
    if (!audio) return;
    setPhase("transcribing");
    // Sem o teto de 60s do apiFetch: uma consulta longa demora para transcrever.
    const res = await request(() => chatService.transcribeLong(apiFetchForm, audio, metadata.email, new AbortController().signal));
    if (!res?.json) {
      setPhase("idle");
      return;
    }
    onFields(res.json);
    setPhase("done");
    notify({ tone: "success", title: t("newOrder.recordedToast"), body: t("newOrder.recordedBody") });
  };

  const transcribing = phase === "transcribing";
  const title = recorder.recording
    ? t("newOrder.recordingTitle")
    : transcribing ? t("newOrder.transcribingTitle")
    : phase === "done" ? t("newOrder.recordedTitle")
    : t("newOrder.recordTitle");
  const body = transcribing ? t("newOrder.transcribingBody") : phase === "done" ? t("newOrder.recordedBody") : t("newOrder.recordBody");

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={transcribing}
      aria-pressed={recorder.recording}
      className={cn(
        "flex w-full items-center gap-3.5 rounded-panel border-strong border-dashed p-4 text-left transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus",
        recorder.recording ? "border-solid border-danger bg-danger-soft" : "border-primary-line bg-surface hover:border-primary hover:bg-primary-tint",
        transcribing && "cursor-wait"
      )}
    >
      <span
        className={cn(
          "grid h-12 w-12 shrink-0 place-items-center rounded-full text-white shadow-cta",
          recorder.recording ? "animate-pulse bg-danger" : "bg-primary"
        )}
        aria-hidden
      >
        {transcribing ? <Spinner size={22} /> : recorder.recording ? <LuSquare size={20} /> : <LuMic size={22} />}
      </span>
      <span className="min-w-0 flex-1">
        <strong className="block text-heading">{title}</strong>
        <span className="text-label text-fg-muted" aria-live="polite">{recorder.recording ? null : body}</span>
      </span>
    </button>
  );
};
